import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus,
  PencilSimple,
  Trash,
  Package,
  Pencil,
  X,
  WarningCircle,
} from '@phosphor-icons/react';
import Alerts from '../../../shared/components/ui/Alerts';
import GenericTables from '../../../shared/components/ui/GenericTables';
import PageHeader from '../../../shared/components/ui/PageHeader';
import { planService } from '../../../services/plans/planService.js';

const EMPTY_FORM = { name: '', storageLimitMB: '', description: '', maxUsers: '', price: '', currency: '' };

const PRICE_PATTERN    = /^\d*(\.\d{0,2})?$/;
const CURRENCY_PATTERN = /^[A-Z]{3}$/;

const sanitizeField = (name, value) => {
  if (name === 'price') {
    const normalized = value.replace(',', '.');
    return PRICE_PATTERN.test(normalized) ? normalized : null;
  }
  if (name === 'currency') return value.replace(/[^a-zA-Z]/g, '').slice(0, 3).toUpperCase();
  return value;
};

const validatePriceAndCurrency = (plan) => {
  const price = String(plan?.price ?? '');
  if (!price || price === '.' || !PRICE_PATTERN.test(price)) return 'El precio debe ser un número con hasta dos decimales';
  if (!CURRENCY_PATTERN.test(plan?.currency ?? '')) return 'La moneda debe tener 3 letras (ej. USD)';
  return null;
};

const fmtStorage = (mb) => {
  if (mb == null) return '--';
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
};

const PlanManagement = () => {
  const [plans,       setPlans]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [pageSize,    setPageSize]    = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems,  setTotalItems]  = useState(0);
  const [totalPages,  setTotalPages]  = useState(1);

  const [newPlan,       setNewPlan]       = useState(EMPTY_FORM);
  const [editPlan,      setEditPlan]      = useState(null);
  const [showModal,     setShowModal]     = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);

  const [showAlerts,       setShowAlerts]       = useState(false);
  const [alertsTitle,      setAlertsTitle]      = useState('Aviso');
  const [alertsContent,    setAlertsContent]    = useState('');
  const [deleteTargetName, setDeleteTargetName] = useState(null);

  const [notification, setNotification] = useState({ show: false, message: '', type: 'success', origin: '' });

  const fetchPlans = async (page = currentPage, size = pageSize) => {
    try {
      setLoading(true);
      const p = Number(page) || 1;
      const s = Number(size) || 10;
      const res = await planService.getPlans({ pageSize: s, pageCurrent: p - 1 });
      const list = res.plans ?? [];
      setPlans(list);
      const pag = res.pagination;
      if (pag) {
        const total = Number(pag.totalItems) || list.length;
        setTotalItems(total);
        setTotalPages(total > 0 ? Math.ceil(total / s) : 1);
      } else {
        setTotalItems(list.length);
        setTotalPages(1);
      }
      setError(null);
    } catch {
      setError('Error al cargar planes');
      setPlans([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, [currentPage, pageSize]);

  const showNotification = (message, type = 'success', origin = '') => {
    setNotification({ show: true, message, type, origin });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success', origin: '' }), 3000);
  };

  const handleInputChange = (e, origin) => {
    const { name } = e.target;
    const value = sanitizeField(name, e.target.value);
    if (value === null) return;
    if (origin === 'create') setNewPlan(prev  => ({ ...prev, [name]: value }));
    else                     setEditPlan(prev  => ({ ...prev, [name]: value }));
  };

  const handleAddPlan = async () => {
    if (!newPlan.name.trim()) {
      showNotification('El nombre del plan es requerido', 'error', 'create'); return;
    }
    if (!newPlan.storageLimitMB || Number(newPlan.storageLimitMB) < 1) {
      showNotification('El almacenamiento debe ser mayor a 0 MB', 'error', 'create'); return;
    }
    if (!newPlan.maxUsers || Number(newPlan.maxUsers) < 1) {
      showNotification('El máximo de usuarios debe ser mayor a 0', 'error', 'create'); return;
    }
    const priceCurrencyError = validatePriceAndCurrency(newPlan);
    if (priceCurrencyError) {
      showNotification(priceCurrencyError, 'error', 'create'); return;
    }
    try {
      await planService.createPlan(newPlan);
      setNewPlan(EMPTY_FORM);
      setShowModal(false);
      await fetchPlans(currentPage, pageSize);
    } catch (err) {
      showNotification(err?.message || 'Error al crear el plan', 'error', 'create');
    }
  };

  const handleEditPlan = async () => {
    if (!editPlan?.storageLimitMB || Number(editPlan.storageLimitMB) < 1) {
      showNotification('El almacenamiento debe ser mayor a 0 MB', 'error', 'edit'); return;
    }
    if (!editPlan?.maxUsers || Number(editPlan.maxUsers) < 1) {
      showNotification('El máximo de usuarios debe ser mayor a 0', 'error', 'edit'); return;
    }
    const priceCurrencyError = validatePriceAndCurrency(editPlan);
    if (priceCurrencyError) {
      showNotification(priceCurrencyError, 'error', 'edit'); return;
    }
    try {
      await planService.updatePlan(editPlan.name, editPlan);
      setEditPlan(null);
      setShowModalEdit(false);
      await fetchPlans(currentPage, pageSize);
    } catch (err) {
      showNotification(err?.message || 'Error al actualizar el plan', 'error', 'edit');
    }
  };

  const handleDeletePlan = async () => {
    try {
      await planService.deletePlan(deleteTargetName);
      await fetchPlans(currentPage, pageSize);
    } catch {
      setError('Error al eliminar el plan');
    }
  };

  const openModal = () => { setNewPlan(EMPTY_FORM); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setNewPlan(EMPTY_FORM); };
  const openModalEdit = (row) => { setEditPlan({ ...row }); setShowModalEdit(true); };
  const closeModalEdit = () => { setShowModalEdit(false); setEditPlan(null); };

  useEffect(() => {
    if (!showModal && !showModalEdit) return undefined;
    const scrollY = window.scrollY;
    const orig = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top:      document.body.style.top,
      width:    document.body.style.width,
    };
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top      = `-${scrollY}px`;
    document.body.style.width    = '100%';
    return () => {
      document.body.style.overflow = orig.overflow;
      document.body.style.position = orig.position;
      document.body.style.top      = orig.top;
      document.body.style.width    = orig.width;
      window.scrollTo(0, scrollY);
    };
  }, [showModal, showModalEdit]);

  const renderModal = (content) => {
    if (typeof document === 'undefined') return null;
    return createPortal(
      <div className="fixed inset-0 z-[10020] bg-[var(--app-overlay)] p-4 overflow-y-auto" role="dialog" aria-modal="true">
        <div className="flex min-h-full items-start justify-center sm:items-center">
          {content}
        </div>
      </div>,
      document.body
    );
  };

  if (error) {
    return (
      <div className="bg-error/12 border border-error rounded-lg p-4 mb-4">
        <div className="flex items-center space-x-2">
          <WarningCircle size={18} className="text-error" />
          <span className="text-error">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Gestión de Planes"
        subtitle="Administra los planes disponibles en el sistema"
        icon={<Package size={28} />}
        actionButton={{
          label: 'Agregar Plan',
          icon: <Plus size={16} />,
          onClick: openModal,
        }}
      />

      <div className="space-y-6">

        {/* Modal crear */}
        {showModal && renderModal(
          <div className="bg-surface rounded-xl shadow-xl border border-line w-full max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto">
            <div className="text-content px-6 py-4 border-b border-line">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center">
                  <Plus size={16} className="mr-2" />
                  Agregar Nuevo Plan
                </h3>
                <button onClick={closeModal} className="text-content-muted hover:text-content transition-colors">
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {notification.origin === 'create' && notification.show && (
                <div className={`mb-4 p-4 rounded-lg ${notification.type === 'success' ? 'bg-success/12 border border-success text-success' : 'bg-error/12 border border-error text-error'}`}>
                  {notification.message}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="sm:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-content mb-1 sm:mb-2">Nombre del plan</label>
                  <input
                    type="text" name="name" autoComplete="off"
                    placeholder="ej. Enterprise"
                    value={newPlan.name ?? ''}
                    onChange={e => handleInputChange(e, 'create')}
                    className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line"
                    maxLength={80}
                  />
                  <label className="block text-xs font-medium text-content-muted mt-1">(El nombre es el identificador y no se puede modificar)</label>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-content mb-1 sm:mb-2">Almacenamiento limite (MB)</label>
                  <input
                    type="number" name="storageLimitMB" autoComplete="off"
                    placeholder="ej. 1024" min={1}
                    value={newPlan.storageLimitMB ?? ''}
                    onChange={e => handleInputChange(e, 'create')}
                    className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-content mb-1 sm:mb-2">Maximo de usuarios</label>
                  <input
                    type="number" name="maxUsers" autoComplete="off"
                    placeholder="ej. 50" min={1}
                    value={newPlan.maxUsers ?? ''}
                    onChange={e => handleInputChange(e, 'create')}
                    className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-content mb-1 sm:mb-2">Precio</label>
                  <input
                    type="text" inputMode="decimal" name="price" autoComplete="off"
                    placeholder="ej. 19.99"
                    value={newPlan.price ?? ''}
                    onChange={e => handleInputChange(e, 'create')}
                    className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-content mb-1 sm:mb-2">Moneda</label>
                  <input
                    type="text" name="currency" autoComplete="off"
                    placeholder="ej. USD" maxLength={3}
                    value={newPlan.currency ?? ''}
                    onChange={e => handleInputChange(e, 'create')}
                    className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line uppercase"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-content mb-1 sm:mb-2">Descripcion</label>
                  <textarea
                    name="description" autoComplete="off"
                    placeholder="Descripcion del plan..."
                    value={newPlan.description ?? ''}
                    onChange={e => handleInputChange(e, 'create')}
                    rows={3} maxLength={500}
                    className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line resize-y"
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button onClick={closeModal} className="px-4 py-2 text-content-muted bg-surface-2 rounded-md hover:bg-surface flex items-center justify-center space-x-2">
                  <span>Cancelar</span>
                </button>
                <button onClick={handleAddPlan} className="bg-amber hover:bg-amber-hover text-on-amber px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2">
                  <span>Agregar Plan</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal editar */}
        {showModalEdit && editPlan && renderModal(
          <div className="bg-surface rounded-xl shadow-xl border border-line w-full max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto">
            <div className="text-content px-6 py-4 border-b border-line">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center">
                  <Pencil size={16} className="mr-2" />
                  Editar Plan
                </h3>
                <button onClick={closeModalEdit} className="text-content-muted hover:text-content transition-colors">
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {notification.origin === 'edit' && notification.show && (
                <div className={`mb-4 p-4 rounded-lg ${notification.type === 'success' ? 'bg-success/12 border border-success text-success' : 'bg-error/12 border border-error text-error'}`}>
                  {notification.message}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="sm:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-content mb-1 sm:mb-2">Nombre del plan</label>
                  <input
                    type="text" name="name"
                    value={editPlan.name ?? ''}
                    disabled
                    className="w-full px-3 py-2 rounded-md bg-surface-2 text-content text-sm border border-line opacity-50 cursor-not-allowed"
                  />
                  <label className="block text-xs font-medium text-content-muted mt-1">(El nombre es el identificador y no se puede modificar)</label>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-content mb-1 sm:mb-2">Almacenamiento limite (MB)</label>
                  <input
                    type="number" name="storageLimitMB" autoComplete="off"
                    placeholder="ej. 1024" min={1}
                    value={editPlan.storageLimitMB ?? ''}
                    onChange={e => handleInputChange(e, 'edit')}
                    className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-content mb-1 sm:mb-2">Maximo de usuarios</label>
                  <input
                    type="number" name="maxUsers" autoComplete="off"
                    placeholder="ej. 50" min={1}
                    value={editPlan.maxUsers ?? ''}
                    onChange={e => handleInputChange(e, 'edit')}
                    className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-content mb-1 sm:mb-2">Precio</label>
                  <input
                    type="text" inputMode="decimal" name="price" autoComplete="off"
                    placeholder="ej. 19.99"
                    value={editPlan.price ?? ''}
                    onChange={e => handleInputChange(e, 'edit')}
                    className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-content mb-1 sm:mb-2">Moneda</label>
                  <input
                    type="text" name="currency" autoComplete="off"
                    placeholder="ej. USD" maxLength={3}
                    value={editPlan.currency ?? ''}
                    onChange={e => handleInputChange(e, 'edit')}
                    className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line uppercase"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-content mb-1 sm:mb-2">Descripcion</label>
                  <textarea
                    name="description" autoComplete="off"
                    placeholder="Descripcion del plan..."
                    value={editPlan.description ?? ''}
                    onChange={e => handleInputChange(e, 'edit')}
                    rows={3} maxLength={500}
                    className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line resize-y"
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button onClick={closeModalEdit} className="px-4 py-2 text-content-muted bg-surface-2 rounded-md hover:bg-surface flex items-center justify-center space-x-2">
                  <span>Cancelar</span>
                </button>
                <button onClick={handleEditPlan} className="bg-amber hover:bg-amber-hover text-on-amber px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2">
                  <span>Guardar Cambios</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabla */}
        <div className="relative task-section md:overflow-hidden md:rounded-xl md:border md:border-line md:bg-surface md:shadow-md">
          <GenericTables
            columns={[
              {
                key: 'name',
                label: 'Plan',
                align: 'center',
                render: (row) => row.name || '--',
              },
              {
                key: 'storageLimitMB',
                label: 'Almacenamiento',
                align: 'center',
                render: (row) => fmtStorage(row.storageLimitMB),
              },
              {
                key: 'maxUsers',
                label: 'Max. usuarios',
                align: 'center',
                render: (row) => row.maxUsers ?? '--',
              },
              {
                key: 'description',
                label: 'Descripcion',
                align: 'center',
                render: (row) => row.description || '--',
              },
              {
                key: 'actions',
                label: 'Acciones',
                align: 'center',
                isAction: true,
                render: (row) => (
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); openModalEdit(row); }}
                      className="bg-surface-2 hover:bg-surface text-content-muted px-3 py-1 rounded-lg text-xs font-medium transition-all duration-200"
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTargetName(row.name);
                        setAlertsTitle('Confirmar Eliminacion');
                        setAlertsContent('¿Estás seguro de que quieres eliminar este plan?');
                        setShowAlerts(true);
                      }}
                      className="bg-error hover:opacity-90 text-white px-3 py-1 rounded-lg text-xs font-medium transition-all duration-200"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                ),
              },
            ]}
            data={plans}
            showFooter={true}
            pagination={{
              currentPage,
              totalPages,
              pageSize,
              totalItems,
              pageSizeOptions: [5, 10, 20, 50],
              onPageChange: setCurrentPage,
              onPageSizeChange: (newSize) => { setPageSize(newSize); setCurrentPage(1); },
            }}
            onRowClick={(row) => openModalEdit(row)}
          />
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-bg/60">
              <div className="flex items-center gap-3 rounded-lg border border-line bg-surface/90 px-4 py-3 text-sm text-content-muted shadow-lg">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-amber border-t-transparent" />
                Consultando planes...
              </div>
            </div>
          )}
        </div>

        {/* Confirm delete */}
        {showAlerts && (
          <Alerts title={alertsTitle} onClose={() => setShowAlerts(false)} width="max-w-md">
            <h2 className="text-lg font-semibold text-content">{alertsContent}</h2>
            <div className="flex justify-end space-x-3 pt-6 border-t border-line">
              <button
                type="button"
                onClick={() => { setShowAlerts(false); setDeleteTargetName(null); }}
                className="px-4 py-2 text-content-muted bg-surface-2 rounded-md hover:bg-surface"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-error text-white rounded-md hover:opacity-90"
                onClick={() => { handleDeletePlan(); setShowAlerts(false); }}
              >
                Eliminar
              </button>
            </div>
          </Alerts>
        )}
      </div>
    </div>
  );
};

export default PlanManagement;
